import { supabase } from "@/integrations/supabase/client";

export type PublishChannel = "instagram" | "tiktok" | "facebook" | "linkedin" | "threads" | "youtube";

interface PublishToMetricoolParams {
  userId: string;
  videoContentId: string;
  channel: PublishChannel;
  publishNow?: boolean;
  scheduledAt?: string;
}

interface CrossPostParams {
  userId: string;
  videoContentId: string;
  channels: PublishChannel[];
  publishNow?: boolean;
  scheduledAt?: string;
}

interface PublishResult {
  success: boolean;
  error?: string;
  metricoolResponse?: unknown;
  publicationId?: string;
  publicationIds?: string[];
  channels?: PublishChannel[];
  scheduledAt?: string;
}

/**
 * Publish content to a social media platform via Metricool
 */
export async function publishToMetricool({
  userId,
  videoContentId,
  channel,
  publishNow = true,
  scheduledAt
}: PublishToMetricoolParams): Promise<PublishResult> {
  try {
    const { data, error } = await supabase.functions.invoke("publish-to-metricool", {
      body: {
        user_id: userId,
        video_content_id: videoContentId,
        channel,
        publish_now: publishNow,
        scheduled_at: scheduledAt
      }
    });

    if (error) {
      console.error("[publishToMetricool] Supabase function error:", error);
      return {
        success: false,
        error: error.message || "Failed to call publish function"
      };
    }

    if (!data?.success) {
      return {
        success: false,
        error: data?.error || "Unknown error from publish function"
      };
    }

    return {
      success: true,
      metricoolResponse: data.metricool_response,
      publicationId: data.publication_id,
      scheduledAt: data.scheduled_at
    };
  } catch (error) {
    console.error("[publishToMetricool] Error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error"
    };
  }
}

/**
 * Cross-post content to multiple platforms in a single Metricool API call
 * This is more efficient than publishing to each channel separately
 */
export async function crossPostToChannels({
  userId,
  videoContentId,
  channels,
  publishNow = true,
  scheduledAt
}: CrossPostParams): Promise<PublishResult> {
  try {
    const { data, error } = await supabase.functions.invoke("publish-to-metricool", {
      body: {
        user_id: userId,
        video_content_id: videoContentId,
        channels, // Send array of channels for cross-posting
        publish_now: publishNow,
        scheduled_at: scheduledAt
      }
    });

    if (error) {
      console.error("[crossPostToChannels] Supabase function error:", error);
      return {
        success: false,
        error: error.message || "Failed to call publish function"
      };
    }

    if (!data?.success) {
      return {
        success: false,
        error: data?.error || "Unknown error from publish function"
      };
    }

    return {
      success: true,
      metricoolResponse: data.metricool_response,
      publicationIds: data.publication_ids,
      channels: data.channels,
      scheduledAt: data.scheduled_at
    };
  } catch (error) {
    console.error("[crossPostToChannels] Error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error"
    };
  }
}

/**
 * Publish content to multiple platforms at once (legacy - uses parallel single calls)
 * @deprecated Use crossPostToChannels for better efficiency
 */
export async function publishToMultipleChannels({
  userId,
  videoContentId,
  channels,
  publishNow = true,
  scheduledAt
}: {
  userId: string;
  videoContentId: string;
  channels: PublishChannel[];
  publishNow?: boolean;
  scheduledAt?: string;
}): Promise<{ results: Record<PublishChannel, PublishResult> }> {
  const results: Record<string, PublishResult> = {};

  // Publish to all channels in parallel
  await Promise.all(
    channels.map(async (channel) => {
      results[channel] = await publishToMetricool({
        userId,
        videoContentId,
        channel,
        publishNow,
        scheduledAt
      });
    })
  );

  return { results: results as Record<PublishChannel, PublishResult> };
}
